import {
	Component, OnInit, Input, ViewChild, ElementRef, effect, HostListener,
	ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { LeadMagnetService, type LeadMagnetPayload, type LeadMagnetResponse } from '../../core/services/data/lead-magnet.service';
import { PurchaseProcessService } from '../../core/services/data/purchase-process.service';
import { UserService } from '../../core/services/data/user.service';
import { ModalService } from '../../core/services/modal.service';
import { GenreService } from '../../core/services/data/genre.service';
import { DiscountService } from '../../core/services/data/discount.service';
import { environment } from '../../../environments/environment';
import { SitesService } from '../../core/services/data/sites.service';
import { PosthogService } from '../../core/services/posthog.service';
import type { ILeadMagnetInvalid, LeadMagnetAssetType } from '../../types/datatypes';
import type { IErrorModalOptions } from '../new-error-modal/new-error-modal.component';

type AssetTypeOption = {
	name: string;
	type: LeadMagnetAssetType;
};

@Component({
	selector: 'app-lead-magnet-form',
	templateUrl: './lead-magnet-form.component.html',
	styleUrls: ['./lead-magnet-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class LeadMagnetFormComponent implements OnInit {
	@Input() featureId: string;

	readonly explainModalId = 'lead-magnet-explain-modal';

	readonly validationErrorModalId = 'lm-validation-error-modal';

	readonly validationErrorOptions: IErrorModalOptions = {
		title: 'This EPUB can\'t be uploaded yet',
		text: ['We found some problems. Please fix them and upload your file again.'],
		actions: [
			{ type: 'solid', text: 'Got it', callback: () => this.modalService.close(this.validationErrorModalId) },
			{ type: 'text', text: 'Contact Support', callback: () => this.modalService.open('contactFormModal') },
		],
	};

	readonly validationFixHint = 'Fix these in the tool you used to create your book (e.g. Vellum, Atticus, Calibre or Sigil), '
		+ 'export a new EPUB and upload it again.';

	@ViewChild('coverInput') coverInput: ElementRef<HTMLInputElement>;

	@ViewChild('assetInput') assetInput: ElementRef<HTMLInputElement>;

	@ViewChild('headlineTooltip') headlineTooltip: ElementRef<HTMLElement>;

	@ViewChild('taglineTooltip') taglineTooltip: ElementRef<HTMLElement>;

	@ViewChild('headlineToggle') headlineToggle: ElementRef<HTMLElement>;

	@ViewChild('taglineToggle') taglineToggle: ElementRef<HTMLElement>;

	public form = new FormGroup({
		funding: new FormControl<'STANDARD' | 'SHARED'>('STANDARD', { nonNullable: true, validators: [Validators.required] }),
		kdpSelectTerms: new FormControl<boolean>(true),
		rightsCertification: new FormControl<boolean>(false, { validators: [Validators.requiredTrue] }),
		genre: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
		asset: new FormGroup({
			type: new FormControl<LeadMagnetAssetType>('FULL_BOOK', { nonNullable: true, validators: [Validators.required] }),
			link: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
		}),
		landingPage: new FormGroup({
			headline: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
			tagline: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
			title: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
			author: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
			description: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
			cover: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
		}),
	});

	public purchaserInfoForm = new FormGroup({
		email: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
		firstName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
		lastName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
		loggedIn: new FormControl<boolean>(false),
	});

	public step = 1;

	public loading = false;

	public saving = false;

	public coverUploading = false;

	public assetUploading = false;

	public assetValidating = false;

	public coverUploadError: string = null;

	public assetUploadError: string = null;

	public assetValidationErrors: ILeadMagnetInvalid['details'] = [];

	public saveError: string = null;

	public assetName: string = null;

	public showPurchaserInfo = false;

	public hostSite = 'Bargain Booksy';

	public showHeadlineDetails = false;

	public showTaglineDetails = false;

	public guideLink = null;

	public standardMemberDiscountAmount = 0;

	public sharedMemberDiscountAmount = 0;

	readonly OTHER_GENRE = 'Other';

	public genreOptions: string[] = [];

	private readonly purchaserInfo = this.us.selectPurchaserInfo();

	private savedId: string = null;

	private savedFeatureRef: string = null;

	get fullPrice(): number {
		return this.pps.siteAdvert.price;
	}

	get sharedPrice(): number {
		return this.lms.getSharedPrice(this.pps.siteAdvert);
	}

	get assetTypeOptions(): AssetTypeOption[] {
		const allowedAssetTypes = this.pps.siteAdvert?.leadMagnet?.allowedAssetTypes;

		if (allowedAssetTypes?.length) return allowedAssetTypes;

		return Object.entries(this.lms.typeToLabel).map(([type, name]) => ({ name, type: type as LeadMagnetAssetType }));
	}

	get selectedAssetType(): LeadMagnetAssetType {
		return this.form.get('asset.type').value;
	}

	get selectedTypeLabel(): string {
		const selectedOption = this.assetTypeOptions.find(({ type }) => type === this.selectedAssetType);

		return selectedOption?.name ?? this.lms.typeToLabel[this.selectedAssetType] ?? 'Full Book';
	}

	get selectedFunding(): 'STANDARD' | 'SHARED' {
		return this.form.get('funding').value;
	}

	get landingPageUrl(): string | null {
		return this.savedId ? `bargainbooksy.com/lp/${this.savedId}` : null;
	}

	get hasAsset(): boolean {
		const field = this.form.get('asset.link');

		return field.value !== '';
	}

	get sharedDiscount(): number {
		return this.pps?.siteAdvert?.leadMagnet?.sharedDiscountPercentage || 50;
	}

	get standardMemberPrice(): number {
		return this.fullPrice - this.standardMemberDiscountAmount;
	}

	get sharedMemberPrice(): number {
		return this.sharedPrice - this.sharedMemberDiscountAmount;
	}

	constructor(
		private ss: SitesService,
		private lms: LeadMagnetService,
		public pps: PurchaseProcessService,
		private route: ActivatedRoute,
		private router: Router,
		private us: UserService,
		public modalService: ModalService,
		private gs: GenreService,
		private discountService: DiscountService,
		private posthogService: PosthogService,
	) {
		effect(() => {
			this.purchaserInfoForm.patchValue(this.purchaserInfo());
			this.showPurchaserInfo = this.purchaserInfoForm.invalid;
		});
	}

	ngOnInit() {
		forkJoin([
			this.gs.getGenres().pipe(first()),
			// This should be wired up to match hostSite, when we expand out of BBY.
			this.ss.getSiteBySlug('bargain-booksy').pipe(first()),
		]).subscribe(([genres, site]) => {
			const siteGenreIds = site?._siteGenres ?? [];
			const targetGenreNames = this.gs.getGenreNamesByIds(genres, siteGenreIds);

			this.genreOptions = [...targetGenreNames, this.OTHER_GENRE];
		});
		this.loadMemberDiscounts();
		this.ensureAllowedAssetType();

		if (this.pps.isEdit) {
			this.featureId = this.pps.featureId;
		}

		if (!this.featureId) return;
		this.loading = true;
		this.lms.getByFeatureId(this.featureId).subscribe((res) => {
			this.loading = false;
			if (res.success) {
				const {
					_id,
					_feature,
					funding,
					asset,
					landingPage,
					kdpSelectTerms,
					rightsCertification,
					genre,
				} = res.leadMagnet;
				this.savedId = _id;
				this.savedFeatureRef = _feature._id;
				if (asset.link) {
					this.assetName = asset.link.replace('https://storage.googleapis.com/wwm-lead-magnets/assets/', '');
				}

				this.form.patchValue({ funding, asset, genre, landingPage, kdpSelectTerms, rightsCertification });
			}
		});
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		const target = event.target as Node;

		if (this.showHeadlineDetails
			&& !this.headlineTooltip?.nativeElement.contains(target)
			&& !this.headlineToggle?.nativeElement.contains(target)) {
			this.showHeadlineDetails = false;
		}

		if (this.showTaglineDetails
				&& !this.taglineTooltip?.nativeElement.contains(target)
				&& !this.taglineToggle?.nativeElement.contains(target)) {
			this.showTaglineDetails = false;
		}
	}

	selectFunding(funding: 'STANDARD' | 'SHARED') {
		this.form.get('funding').setValue(funding);
	}

	onExplainClick() {
		this.modalService.open(this.explainModalId);
	}

	onStep1Continue() {
		if (this.form.invalid || this.purchaserInfoForm.invalid) {
			this.form.markAllAsTouched();
			this.purchaserInfoForm.markAllAsTouched();
			return;
		}
		this.posthogService.capture(this.posthogService.LEAD_MAGNET_INFO_ENTERED, {
			product_type: 'leadMagnet',
			product_id: this.pps.siteAdvert?._id,
			product_name: this.pps.siteAdvert?.name,
			site: this.hostSite,
			is_edit: this.pps.isEdit,
			title: this.form.get('landingPage.title')?.value ?? 'unknown',
			genre: this.form.get('genre')?.value ?? 'unknown',
			// @ts-expect-error Fallback is not a real type error
			asset_type: this.form.get('asset.type')?.value ?? 'unknown',
			kdp_select_terms: this.form.get('kdpSelectTerms')?.value ?? false,
			rights_certification: this.form.get('rightsCertification')?.value ?? false,
			landing_page_headline: this.form.get('landingPage.headline')?.value ?? 'unknown',
			landing_page_tagline: this.form.get('landingPage.tagline')?.value ?? 'unknown',
			landing_page_description: this.form.get('landingPage.description')?.value ?? 'unknown',
		});
		this.step = 2;
	}

	onBack() {
		this.step = 1;
	}

	selectAssetType(type: LeadMagnetAssetType) {
		this.form.get('asset.type').setValue(type);
	}

	triggerCoverUpload() {
		this.coverInput.nativeElement.click();
	}

	triggerAssetUpload() {
		this.assetInput.nativeElement.click();
	}

	onCoverFileChange(event: Event) {
		const file = (event.target as HTMLInputElement).files?.[0];
		if (!file) return;
		this.coverUploading = true;
		this.coverUploadError = null;
		this.lms.uploadCover(file).subscribe((res) => {
			this.coverUploading = false;
			if (res.success) {
				this.form.get('landingPage.cover').setValue(res.url);
			} else {
				this.coverUploadError = 'Cover upload failed. Please try again.';
			}
		});
	}

	async onAssetFileChange(event: Event) {
		if (this.assetUploading) return;
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		// Reset the input so picking the same file again still triggers a change event.
		input.value = '';
		this.assetUploading = true;
		this.assetValidating = true;
		this.assetUploadError = null;
		const validationResults = await this.lms.validateAsset(file);
		this.assetValidating = false;
		if (validationResults.valid === false) {
			/**
			 * Display validation errors in a modal.
			 */
			this.assetValidationErrors = validationResults.details;
			this.modalService.open(this.validationErrorModalId);
			this.assetUploading = false;
			return;
		}
		this.lms.uploadAsset(file).subscribe((res) => {
			this.assetUploading = false;
			if (res.success) {
				this.form.get('asset.link').setValue(res.url);
				this.assetName = file.name;
			} else {
				this.assetUploadError = 'Asset upload failed. Please try again.';
			}
		});
	}

	onPreview() {
		const params = new URLSearchParams({
			lm_cover: this.form.get('landingPage.cover').value,
			lm_author: this.form.get('landingPage.author').value,
			lm_headline: this.form.get('landingPage.headline').value,
			lm_tagline: this.form.get('landingPage.tagline').value,
			lm_description: this.form.get('landingPage.description').value,
			lm_asset_type: this.form.get('asset.type').value,
			lm_funding: this.form.get('funding').value,
		});
		const previewUrl = `${environment.bargainbooksyURL}/lp/preview/?${params.toString()}`;
		window.open(previewUrl, '_blank');
	}

	onContinue() {
		this.posthogService.capture(this.posthogService.LEAD_MAGNET_FUNDING_SELECTED, {
			product_type: 'leadMagnet',
			product_id: this.pps.siteAdvert?._id,
			product_name: this.pps.siteAdvert?.name,
			site: this.hostSite,
			funding: this.selectedFunding,
			price: this.selectedFunding === 'STANDARD' ? this.standardMemberPrice : this.sharedMemberPrice,
		});
		this.saving = true;
		this.saveError = null;
		this.lms.upsert(this.buildPayload()).subscribe((res: { success: boolean; leadMagnet: LeadMagnetResponse }) => {
			this.saving = false;
			if (res.success) {
				this.savedId = res.leadMagnet._id;
				this.proceedToCheckout(res);
			} else {
				this.saveError = 'Something went wrong. Please try again.';
			}
		});
	}

	isFieldInvalid(path: string): boolean {
		const field = this.form.get(path);

		if (field) {
			return field.touched && field.invalid;
		}

		const purchaserField = this.purchaserInfoForm.get(path);

		return purchaserField.touched && purchaserField.invalid;
	}

	removeAsset(): void {
		this.assetName = null;
		this.form.get('asset.link').setValue('');
	}

	public writeItForMe(type: 'headline' | 'tagline') {
		const writeItFormMeText = `--- Write the ${type} for me please ---`;

		if (type === 'headline') {
			this.form.get('landingPage.headline').patchValue(writeItFormMeText);
		}

		if (type === 'tagline') {
			this.form.get('landingPage.tagline').patchValue(writeItFormMeText);
		}
	}

	private ensureAllowedAssetType() {
		const [firstOption] = this.assetTypeOptions;
		const isAllowed = this.assetTypeOptions.some(({ type }) => type === this.selectedAssetType);

		if (isAllowed || !firstOption) return;

		this.selectAssetType(firstOption.type);
	}

	private async loadMemberDiscounts() {
		const [standard, shared] = await Promise.all([
			this.discountService.getMemberDiscount(this.fullPrice),
			this.discountService.getMemberDiscount(this.sharedPrice),
		]);
		this.standardMemberDiscountAmount = standard.discountAmount;
		this.sharedMemberDiscountAmount = shared.discountAmount;
	}

	private proceedToCheckout(res: { success: boolean; leadMagnet: LeadMagnetResponse }) {
		this.pps.feature = res.leadMagnet._feature;
		const path = `../checkout/${res.leadMagnet._feature._id}`;
		this.router.navigate([path], { relativeTo: this.route });
	}

	private buildPayload(): LeadMagnetPayload {
		return {
			leadMagnet: {
				...(this.savedId && { _id: this.savedId }),
				...(this.savedFeatureRef && { _feature: this.savedFeatureRef }),
				...this.form.getRawValue(),
			},
			feature: {
				pubDate: this.pps.pubDate,
				_siteAdvert: this.pps.siteAdvert._id,
				purchaserInfo: this.purchaserInfoForm.getRawValue(),
			},
		};
	}
}
